import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Pencil,
  RotateCcw,
  Save,
  X,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

type PromptRow = {
  id: string;
  title: string;
  description: string;
  source: string;
  usedFor: string;
  content: string;
  defaultContent: string;
  isOverridden: boolean;
  updatedAt: Date | null;
};

export function PromptsPage() {
  const list = trpc.prompts.list.useQuery();
  const utils = trpc.useUtils();
  const updateMut = trpc.prompts.update.useMutation({
    onSuccess: () => utils.prompts.list.invalidate(),
  });
  const resetMut = trpc.prompts.reset.useMutation({
    onSuccess: () => utils.prompts.list.invalidate(),
  });

  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const toggle = (id: string) => {
    if (editingId === id) return;
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startEdit = (p: PromptRow) => {
    setEditingId(p.id);
    setDraft(p.content);
    setOpenIds((prev) => ({ ...prev, [p.id]: true }));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft("");
  };

  const save = async (id: string) => {
    try {
      const res = await updateMut.mutateAsync({ id: id as never, content: draft });
      toast.success(res.reset ? "Reset to default" : "Prompt saved");
      setEditingId(null);
      setDraft("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    }
  };

  const reset = async (id: string) => {
    if (!confirm("Reset the prompt to the default text?")) return;
    try {
      await resetMut.mutateAsync({ id: id as never });
      toast.success("Reset to default");
      if (editingId === id) {
        setEditingId(null);
        setDraft("");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reset");
    }
  };

  const copy = async (id: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      toast.success("Prompt copied");
      setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  };

  useEffect(() => {
    if (!editingId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingId]);

  const prompts = (list.data ?? []) as PromptRow[];

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:px-6">
      <div className="mb-6 flex items-center gap-3">
        <Sparkles className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Prompts</h1>
          <p className="text-sm text-muted-foreground">
            AI system prompts. Changes take effect immediately.
          </p>
        </div>
      </div>

      {list.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : prompts.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No prompts found
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {prompts.map((p) => {
            const open = !!openIds[p.id] || editingId === p.id;
            const isEditing = editingId === p.id;
            const isCopied = copiedId === p.id;
            const isSaving = updateMut.isPending && updateMut.variables?.id === p.id;
            const isResetting = resetMut.isPending && resetMut.variables?.id === p.id;
            return (
              <div key={p.id} className="rounded-lg border bg-card">
                <div className="flex items-start justify-between gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    className="flex flex-1 items-start gap-2 text-left"
                    disabled={isEditing}
                  >
                    {open ? (
                      <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold">{p.title}</h2>
                        <Badge variant="secondary" className="font-mono text-xs">
                          {p.id}
                        </Badge>
                        {p.isOverridden && (
                          <Badge variant="outline" className="text-xs">
                            Modified
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                      <dl className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-[auto,1fr] sm:gap-x-3">
                        <dt className="font-medium">Source:</dt>
                        <dd className="font-mono">{p.source}</dd>
                        <dt className="font-medium">Used for:</dt>
                        <dd>{p.usedFor}</dd>
                        {p.updatedAt && (
                          <>
                            <dt className="font-medium">Updated:</dt>
                            <dd>{new Date(p.updatedAt).toLocaleString("en-US")}</dd>
                          </>
                        )}
                      </dl>
                    </div>
                  </button>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    {!isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copy(p.id, p.content)}
                      >
                        {isCopied ? (
                          <Check className="mr-1.5 h-3.5 w-3.5" />
                        ) : (
                          <Copy className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {isCopied ? "Copied" : "Copy"}
                      </Button>
                    )}
                    {!isEditing ? (
                      <Button variant="outline" size="sm" onClick={() => startEdit(p)}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                        Edit
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={cancelEdit}>
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
                {open && (
                  <div className="border-t bg-muted/40">
                    {isEditing ? (
                      <div className="flex flex-col gap-3 p-4">
                        <Textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          className="min-h-[320px] font-mono text-xs leading-relaxed"
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs text-muted-foreground">
                            {draft.length.toLocaleString("en-US")} characters
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {p.isOverridden && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => reset(p.id)}
                                disabled={isResetting || isSaving}
                              >
                                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                                {isResetting ? "Resetting…" : "Reset to default"}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => save(p.id)}
                              disabled={isSaving || draft.trim().length === 0}
                            >
                              <Save className="mr-1.5 h-3.5 w-3.5" />
                              {isSaving ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <pre className="overflow-x-auto whitespace-pre-wrap break-words px-4 py-3 font-mono text-xs leading-relaxed text-foreground">
                        {p.content}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
